import React, { useEffect } from "react"
import { useSelector } from "react-redux"

import { NOW, NEXT_2_DAYS } from "const"
import {
    StoreType,
    TaskFilters,
    TaskFiltersStatus,
} from "client/store"
import {
    SearchBar,
    SearchFilter,
} from "client/components"
import {
    SearchTaskForm,
    TaskFilterForm,
} from "./filterForm"


const initStatus: TaskFiltersStatus = {
    pending: false,
    inProgress: false,
    pendingReview: false,
    completed: false,
    deferred: false,
}
const inProgressStatus: TaskFiltersStatus = {
    pending: true,
    inProgress: true,
    pendingReview: true,
    completed: false,
    deferred: true,
}
const completedStatus: TaskFiltersStatus = {
    pending: false,
    inProgress: false,
    pendingReview: false,
    completed: true,
    deferred: false,
}
const postponedStatus: TaskFiltersStatus = {
    pending: false,
    inProgress: false,
    pendingReview: false,
    completed: false,
    deferred: true,
}
const pendingReviewStatus: TaskFiltersStatus = {
    pending: false,
    inProgress: false,
    pendingReview: true,
    completed: false,
    deferred: false,
}

const predefinedStatusArray: TaskFiltersStatus[] = [inProgressStatus, completedStatus, postponedStatus, pendingReviewStatus]

const compareStatus = (status1: TaskFiltersStatus, status2: TaskFiltersStatus) => (
    status1 && status2
    && status1.pending === status2.pending
    && status1.inProgress === status2.inProgress
    && status1.pendingReview === status2.pendingReview
    && status1.completed === status2.completed
    && status1.deferred === status2.deferred
)


interface SearchTask extends SearchTaskForm {
    id: string
    leftOffset?: boolean
    rightOffset?: boolean
}

const TaskSearch = ({
    id,
    leftOffset = false,
    rightOffset = false,
    filters,
    onFilter,
}: SearchTask) => {
    const projectList = useSelector((state: StoreType) => state.project.projectList)
    const userList = useSelector((state: StoreType) => state.user.userList)
    const lang = useSelector((state: StoreType) => state.system.lang)

    useEffect(() => {
        // set "inProgressStatus" on init
        onFilter({
            ...filters,
            status: inProgressStatus,
        })
    }, [])

    // popup filters

    const inProgressFilter: SearchFilter = {
        title: lang.inProgress,
        isActive: compareStatus(filters.status, inProgressStatus),
        onAdd: (obj: TaskFilters) => ({
            ...obj,
            status: inProgressStatus,
        }),
        onClose: (obj: TaskFilters) => ({
            ...obj,
            status: initStatus,
        }),
    }
    const completedFilter: SearchFilter = {
        title: lang.completed,
        isActive: compareStatus(filters.status, completedStatus),
        onAdd: (obj: TaskFilters) => ({
            ...obj,
            status: completedStatus,
        }),
        onClose: (obj: TaskFilters) => ({
            ...obj,
            status: inProgressStatus,
        }),
    }
    const postponedFilter: SearchFilter = {
        title: lang.postponed,
        isActive: compareStatus(filters.status, postponedStatus),
        onAdd: (obj: TaskFilters) => ({
            ...obj,
            status: postponedStatus,
        }),
        onClose: (obj: TaskFilters) => ({
            ...obj,
            status: inProgressStatus,
        }),
    }
    const pendingReviewFilter: SearchFilter = {
        title: lang.pendingReview,
        isActive: compareStatus(filters.status, pendingReviewStatus),
        onAdd: (obj: TaskFilters) => ({
            ...obj,
            status: pendingReviewStatus,
        }),
        onClose: (obj: TaskFilters) => ({
            ...obj,
            status: inProgressStatus,
        }),
    }

    const popupFilters: SearchFilter[] = [
        inProgressFilter,
        completedFilter,
        postponedFilter,
        {
            title: lang.overdue,
            isActive: filters.overdue === NOW,
            onAdd: (obj: TaskFilters) => ({
                ...obj,
                overdue: NOW,
            }),
            onClose: (obj: TaskFilters) => ({
                ...obj,
                overdue: null,
            }),
        },
        {
            title: lang.almostOverdue,
            isActive: filters.overdue === NEXT_2_DAYS,
            onAdd: (obj: TaskFilters) => ({
                ...obj,
                overdue: NEXT_2_DAYS,
            }),
            onClose: (obj: TaskFilters) => ({
                ...obj,
                overdue: null,
            }),
        },
        pendingReviewFilter,
    ]

    // extended filters

    const extendedFilters: SearchFilter[] = []

    if (filters.name && filters.name.length) {
        extendedFilters.push({
            title: `${lang.name}: ${filters.name}`,
            onClose: (obj: TaskFilters) => ({
                ...obj,
                name: null,
            }),
        })
    }
    if (filters.projectId) {
        const selectedProject = (projectList && projectList[filters.projectId]) || null
        const projectName = selectedProject ? `${selectedProject.name} (${selectedProject.publicId})` : ""
        extendedFilters.push({
            title: `${lang.project}: ${projectName}`,
            onClose: (obj: TaskFilters) => ({
                ...obj,
                projectId: null,
            }),
        })
    }
    if (filters.creator) {
        const user = userList[filters.creator]
        extendedFilters.push({
            title: `${lang.createdBy}: ${user.firstName} ${user.lastName}`,
            onClose: (obj: TaskFilters) => ({
                ...obj,
                creator: null,
            }),
        })
    }
    if (filters.responsible) {
        const user = userList[filters.responsible]
        extendedFilters.push({
            title: `${lang.responsible}: ${user.firstName} ${user.lastName}`,
            onClose: (obj: TaskFilters) => ({
                ...obj,
                responsible: null,
            }),
        })
    }
    if (filters.deadline) {
        extendedFilters.push({
            title: `${lang.deadline}: ${lang[filters.deadline]}`,
            onClose: (obj: TaskFilters) => ({
                ...obj,
                deadline: null,
            }),
        })
    }
    if (filters.highPriority) {
        extendedFilters.push({
            title: lang.highPriority,
            onClose: (obj: TaskFilters) => ({
                ...obj,
                highPriority: false,
            }),
        })
    }

    let isStatusEqual = false
    predefinedStatusArray.forEach((status) => {
        if (isStatusEqual) return
        if (compareStatus(status, filters.status)) {
            if (
                (compareStatus(status, inProgressStatus) && inProgressFilter.isActive)
                || (compareStatus(status, completedStatus) && completedFilter.isActive)
                || (compareStatus(status, postponedStatus) && postponedFilter.isActive)
                || (compareStatus(status, pendingReviewStatus) && pendingReviewFilter.isActive)
            ) isStatusEqual = true
        }
    })
    if (!isStatusEqual) {
        const activeStatusArray: string[] = []
        if (filters.status) {
            if (filters.status.pending) activeStatusArray.push(lang.pending)
            if (filters.status.inProgress) activeStatusArray.push(lang.inProgress)
            if (filters.status.pendingReview) activeStatusArray.push(lang.pendingReview)
            if (filters.status.completed) activeStatusArray.push(lang.completed)
            if (filters.status.deferred) activeStatusArray.push(lang.deferred)
        }

        if (activeStatusArray.length > 0) {
            extendedFilters.push({
                title: `${lang.status}: ${activeStatusArray.join(", ")}`,
                onClose: (obj: TaskFilters) => ({
                    ...obj,
                    status: initStatus,
                }),
            })
        }
    }

    return (
        <SearchBar
            id={id}
            filters={filters}
            leftOffset={leftOffset}
            rightOffset={rightOffset}
            popupFilters={popupFilters}
            extendedFilters={extendedFilters}
            filterForm={<TaskFilterForm filters={filters} onFilter={onFilter} />}
            onFilter={onFilter}
        />
    )
}
export default TaskSearch
